import React from 'react';
import type { NumberLineItem } from '../../../../transformers/zod-schemas';
import { toPercent, toHighlightColor } from '../../../utils/number-line.utils';

type HighlightItem = NonNullable<NumberLineItem['highlights']>[number];

export interface HighlightBandsProps {
  highlights: HighlightItem[] | undefined;
  min: number;
  max: number;
}

interface BandGeometry {
  leftPct:  number;
  widthPct: number;
}

/** Pre-compute band geometry outside JSX for readability and testability. */
const getBandGeometry = (h: HighlightItem, min: number, max: number): BandGeometry => {
  const leftPct  = toPercent(Math.min(h.from, h.to), min, max);
  const rightPct = toPercent(Math.max(h.from, h.to), min, max);
  return { leftPct, widthPct: rightPct - leftPct };
};

export const HighlightBands: React.FC<HighlightBandsProps> = ({ highlights, min, max }) => {
  if (!highlights?.length) return null;

  return (
    <>
      {highlights.map(h => {
        const { leftPct, widthPct } = getBandGeometry(h, min, max);
        if (widthPct <= 0) return null;
        return (
          <div
            key={h.id}
            className="number-line-highlight-band"
            aria-hidden="true"
            style={
              {
                left:  `${leftPct}%`,
                width: `${widthPct}%`,
                ...(h.color && { '--nl-highlight-color': toHighlightColor(h.color) }),
              } as React.CSSProperties
            }
          />
        );
      })}
    </>
  );
};
